/**
 * PATH: apps/qa/src/pages/record-detail/ApplyTemplateModal.tsx
 * PURPOSE: Modal for applying checklist templates to inspection areas
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { templateApi, checklistApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { ChecklistTemplate } from '@/types/qa'
import { ListChecks, X, CheckCircle, Loader2 } from 'lucide-react'

interface ApplyTemplateModalProps {
  inspectionId: string
  floors: Record<string, unknown>[]
  onClose: () => void
  onSuccess: () => void
}

export function ApplyTemplateModal({ inspectionId, floors, onClose, onSuccess }: ApplyTemplateModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [selectedAreas, setSelectedAreas] = useState<string[]>([])
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['qa-templates-for-apply'],
    queryFn: () => templateApi.getTemplates(),
  })

  const allAreas = floors?.flatMap((floor: Record<string, unknown>) =>
    ((floor.areas as Record<string, unknown>[]) || []).map((area: Record<string, unknown>) => ({
      ...area, floorName: floor.floor_name,
    }))
  ) || []

  const publishedTemplates = (templates || []).filter((t: ChecklistTemplate) => t.status === 'published')

  const toggleArea = (areaId: string) => {
    setSelectedAreas(prev => prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId])
  }

  const selectAllAreas = () => {
    setSelectedAreas(selectedAreas.length === allAreas.length ? [] : allAreas.map((a: Record<string, unknown>) => a.id as string))
  }

  const handleApply = async () => {
    if (!selectedTemplate || selectedAreas.length === 0) return
    setIsApplying(true); setError(null)
    try {
      await checklistApi.applyTemplate(selectedTemplate, selectedAreas)
      onSuccess()
    } catch (err: unknown) {
      setError(err?.response?.data?.error || err?.message || 'Failed to apply template')
    } finally {
      setIsApplying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl p-6 w-full max-w-lg shadow-xl border border-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-primary" /> Apply Template to Areas
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>
        )}

        {/* Template Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">Select Template</label>
          {templatesLoading ? (
            <div className="h-10 bg-muted animate-pulse rounded-lg" />
          ) : publishedTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No published templates available. <Link to="/templates/new" className="text-primary hover:underline">Create one</Link>
            </p>
          ) : (
            <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary">
              <option value="">Select a template...</option>
              {publishedTemplates.map((template: ChecklistTemplate) => (
                <option key={template.id} value={template.id}>{template.name} ({template.trade || 'General'}) - v{template.version}</option>
              ))}
            </select>
          )}
        </div>

        {/* Area Selection */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-foreground">Select Areas ({selectedAreas.length} selected)</label>
            <button onClick={selectAllAreas} className="text-xs text-primary hover:underline">
              {selectedAreas.length === allAreas.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {allAreas.length === 0 ? (
            <p className="text-sm text-muted-foreground">No areas found. Add floors and areas to the inspection first.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto border border-border rounded-lg p-2">
              {floors?.map((floor: Record<string, unknown>) => (
                <div key={floor.id}>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-2 py-1">{floor.floor_name}</p>
                  {floor.areas?.map((area: Record<string, unknown>) => (
                    <label key={area.id} className={cn(
                      'flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors',
                      selectedAreas.includes(area.id) ? 'bg-primary/10 border border-primary/30' : 'hover:bg-accent border border-transparent',
                    )}>
                      <input type="checkbox" checked={selectedAreas.includes(area.id)} onChange={() => toggleArea(area.id)}
                        className="w-4 h-4 rounded border-input text-primary focus:ring-primary/20" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{area.area_name}</p>
                        <p className="text-xs text-muted-foreground">{area.area_code}</p>
                      </div>
                      <span className={cn('text-xs px-2 py-0.5 rounded-full',
                        area.status === 'locked' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-muted text-muted-foreground')}>
                        {area.status.replace(/_/g, ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors">Cancel</button>
          <button onClick={handleApply} disabled={!selectedTemplate || selectedAreas.length === 0 || isApplying}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isApplying ? (<><Loader2 className="w-4 h-4 animate-spin" /> Applying...</>) : (<><CheckCircle className="w-4 h-4" /> Apply Template</>)}
          </button>
        </div>
      </div>
    </div>
  )
}
